import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { updateFamilyMemberAPI } from "../../../services/Profile/FamilyMember/updateFamilyMember";
import BloodGroupDropdown from "../../../services/Profile/FamilyMember/BloodGroupDropdown"; // ✅ import blood group dropdown

const MASTER_BASE_URL = "https://online-tech.in/api/MasterAPI";

export default function UpdateFamilyMember({ editData, onCancel, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [relations, setRelations] = useState([]);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const userId =
    editData?.userId ||
    JSON.parse(localStorage.getItem("medarphaUser") || "{}")?.id;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    relationId: "",
    age: "",
    gender: "",
    dateOfBirth: "",
    heightCM: "",
    weightKG: "",
    bloodGroup: "", // ✅ blood group field
    address: "",
    isDefault: false,
    uploadPhoto: null,
  });

  /* ================= RELATION LIST ================= */
  useEffect(() => {
    axios
      .get(`${MASTER_BASE_URL}/GetAllRelations`)
      .then((res) => setRelations(res.data || []))
      .catch(() => toast.error("Relation load failed"));
  }, []);

  /* ================= PREFILL DATA ================= */
  useEffect(() => {
    if (!editData) return;

    setForm({
      firstName: editData.firstName || "",
      lastName: editData.lastName || "",
      relationId: editData.relationId || "",
      age: editData.age || "",
      gender: editData.gender || "",
      dateOfBirth: editData.dateOfBirth
        ? editData.dateOfBirth.split("T")[0]
        : "",
      heightCM: editData.heightCM || "",
      weightKG: editData.weightKG || "",
      bloodGroup: editData.bloodGroup || "", // ✅ prefill blood group
      address: editData.address || "",
      isDefault: editData.isDefault || false,
      uploadPhoto: null,
    });

    if (editData.uploadPhoto) {
      setPreviewPhoto(`https://online-tech.in/uploads/${editData.uploadPhoto}`);
    }
  }, [editData]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      setForm((prev) => ({ ...prev, uploadPhoto: files[0] }));
      setPreviewPhoto(URL.createObjectURL(files[0]));
    } else if (name === "dateOfBirth") {
      // ✅ Calculate age automatically
      const today = new Date();
      const dob = new Date(value);
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      setForm((prev) => ({
        ...prev,
        dateOfBirth: value,
        age: age >= 0 ? age : "",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!editData?.id) return toast.error("Member ID missing");
    if (!userId) return toast.error("User ID missing");

    const fd = new FormData();

    fd.append("Id", editData.id);
    fd.append("UserId", userId);
    fd.append("FirstName", form.firstName);
    fd.append("LastName", form.lastName);
    fd.append("RelationId", form.relationId);
    fd.append("Age", form.age || 0);
    fd.append("Gender", form.gender || "");
    fd.append(
      "DateOfBirth",
      form.dateOfBirth ? `${form.dateOfBirth}T00:00:00` : ""
    );
    fd.append("HeightCM", form.heightCM || 0);
    fd.append("WeightKG", form.weightKG || 0);
    fd.append("BloodGroup", form.bloodGroup || "");
    fd.append("Address", form.address || "");
    fd.append("IsDefault", form.isDefault);

    if (form.uploadPhoto) {
      fd.append("UploadPhoto", form.uploadPhoto);
    }

    try {
      setLoading(true);
      const res = await updateFamilyMemberAPI(fd);

      if (res.data?.succeeded) {
        toast.success("Family member updated");
        onSuccess();
      } else {
        toast.error(res.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("🚨 API ERROR:", err.response || err);
      toast.error("Update failed (check console)");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="border p-4 rounded mt-3">
      <h5 className="mb-3">✏️ Update Family Member</h5>

      <form onSubmit={handleUpdate} className="row g-3">
        <div className="col-md-6">
          <label>First Name *</label>
          <input
            className="form-control"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6">
          <label>Last Name</label>
          <input
            className="form-control"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-6">
          <label>Relation *</label>
          <select
            className="form-control"
            name="relationId"
            value={form.relationId}
            onChange={handleChange}
            required
          >
            <option value="">Select</option>
            {relations.map((r) => (
              <option key={r.id} value={r.id}>
                {r.relationName}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label>Age</label>
          <input
            type="number"
            className="form-control"
            name="age"
            value={form.age}
            readOnly // ✅ Age now auto-fills from DOB
          />
        </div>

        <div className="col-md-6">
          <label>Gender</label>
          <select
            className="form-control"
            name="gender"
            value={form.gender}
            onChange={handleChange}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="col-md-6">
          <label>DOB</label>
          <input
            type="date"
            className="form-control"
            name="dateOfBirth"
            value={form.dateOfBirth}
            onChange={handleChange}
          />
        </div>

        {/* ✅ Blood Group */}
        <div className="col-md-6">
          <label>Blood Group</label>
          <BloodGroupDropdown
            value={form.bloodGroup}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-6">
          <label>Photo</label>
          <input type="file" className="form-control" onChange={handleChange} />
          {previewPhoto && (
            <img src={previewPhoto} alt="preview" width={100} className="mt-2" />
          )}
        </div>

        <div className="col-md-6 d-flex align-items-center">
          <input
            type="checkbox"
            className="me-2"
            name="isDefault"
            checked={form.isDefault}
            onChange={handleChange}
          />
          Set as default
        </div>

        <div className="col-12 text-end">
          <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-danger" disabled={loading}>
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
}
